import type { PDFDataRangeTransport, PDFWorker } from 'pdfjs-dist';
import { AlbumContentType } from 'common/enums';
import { adaptContentType } from 'helpers/utils';
import { AlbumContentSerialized } from 'networking/types/album-detail';

// Width the first page is drawn at. Thumbnails are at most ~260px wide, so
// this stays sharp on 2x screens without drawing more than needed.
const PAGE_WIDTH = 520;
// PDFs drawn at the same time; the rest wait for a free slot.
const MAX_RENDERS = 2;
// Smaller PDFs are downloaded in one go; bigger ones only in the parts the
// first page needs.
const MIN_RANGED_SIZE = 1024 * 1024;
// A part that takes longer than this fails the thumbnail, so a stalled
// download can't hold a render slot forever.
const RANGE_TIMEOUT_MS = 60000;

export const isPdfContent = (content: AlbumContentSerialized) =>
  adaptContentType(content) === AlbumContentType.document &&
  !!content.mediaUrl &&
  (content.content.contentType === 'application/pdf' ||
    /\.pdf$/i.test(content.name ?? ''));

const rendered = new Map<string, string>();
const jobs = new Map<string, Promise<string>>();
const waiting: (() => void)[] = [];
let running = 0;
let worker: PDFWorker | null = null;

const takeSlot = async () => {
  if (running >= MAX_RENDERS) {
    await new Promise<void>((resolve) => {
      waiting.push(resolve);
    });
  }
  running += 1;
};

const releaseSlot = () => {
  running -= 1;
  waiting.shift()?.();
};

// Feeds pdf.js only the byte ranges it asks for. pdf.js does this by itself
// only when it can read the storage's Accept-Ranges header, and our Backblaze
// buckets don't expose that header to the browser (CORS). pdf.js then
// downloads the whole file: hundreds of MB for a scanned scrapbook whose first
// page needs about 1 MB. The file's size comes from the API instead.
// `failed` rejects when a download fails, so the render stops waiting.
const createRangeTransport = (
  Transport: typeof PDFDataRangeTransport,
  url: string,
  size: number,
) => {
  const transport = new Transport(size, null);
  const controller = new AbortController();
  // The file URL redirects to a signed storage link. Later parts are
  // downloaded from that link directly instead of via the API each time.
  let target = url;
  // Set if the server ignores ranges and sends the whole file.
  let whole: Uint8Array | null = null;
  // Set once pdf.js is done with the document.
  let closed = false;
  let fail: (error: unknown) => void = () => {};
  const failed = new Promise<never>((_resolve, reject) => {
    fail = reject;
  });
  // A download can fail after the render is over, with nothing awaiting this.
  failed.catch(() => {});

  const download = async (from: string, begin: number, end: number) => {
    const response = await fetch(from, {
      headers: { Range: `bytes=${begin}-${end - 1}` },
      signal: controller.signal,
    });
    if (!response.ok) return null;
    if (response.url) target = response.url;
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (response.status === 206) return bytes;
    whole = bytes;
    return whole.subarray(begin, end);
  };

  const read = async (begin: number, end: number) => {
    if (whole) return whole.subarray(begin, end);
    const timer = window.setTimeout(() => controller.abort(), RANGE_TIMEOUT_MS);
    try {
      let bytes = await download(target, begin, end);
      // The signed link may have expired; the API hands out a fresh one.
      if (!bytes && target !== url) {
        target = url;
        bytes = await download(target, begin, end);
      }
      if (!bytes) throw new Error('Could not download the PDF');
      return bytes;
    } finally {
      window.clearTimeout(timer);
    }
  };

  transport.requestDataRange = (begin, end) => {
    read(begin, end).then(
      (chunk) => {
        if (closed) return;
        try {
          transport.onDataRange(begin, chunk);
        } catch {
          // pdf.js stopped waiting for this part.
        }
      },
      (error) => {
        if (!closed) fail(error);
      },
    );
  };
  transport.abort = () => {
    closed = true;
    controller.abort();
  };

  return { transport, failed };
};

const drawFirstPage = async (url: string, size: number | null) => {
  const pdfjs = await import('pdfjs-dist');
  // Same worker file as common/pdf-viewer; one worker serves every thumbnail.
  pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;
  if (!worker) worker = new pdfjs.PDFWorker();

  const ranged =
    size != null && size > MIN_RANGED_SIZE
      ? createRangeTransport(pdfjs.PDFDataRangeTransport, url, size)
      : null;
  // Without auto-fetch, pdf.js stops asking for data once it has the first
  // page, instead of fetching the rest of the file in the background.
  const task = pdfjs.getDocument({
    ...(ranged ? { range: ranged.transport } : { url }),
    worker,
    disableStream: true,
    disableAutoFetch: true,
  });
  const orFail = <T>(promise: Promise<T>) =>
    ranged ? Promise.race([promise, ranged.failed]) : promise;
  try {
    const pdf = await orFail(task.promise);
    const page = await orFail(pdf.getPage(1));
    const { width } = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: PAGE_WIDTH / width });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    await orFail(page.render({ canvas, viewport }).promise);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.85);
    });
    if (!blob) throw new Error('Could not encode the PDF page');
    return URL.createObjectURL(blob);
  } finally {
    await task.destroy();
  }
};

// The first page of a PDF as an image URL. Each key is drawn once per session
// and reused; a PDF that fails isn't retried until the page reloads. `size`
// is the file's size in bytes, when known.
export const renderPdfFirstPage = (
  url: string,
  key: string,
  size: number | null = null,
) => {
  let job = jobs.get(key);
  if (!job) {
    job = (async () => {
      await takeSlot();
      try {
        const src = await drawFirstPage(url, size);
        rendered.set(key, src);
        return src;
      } finally {
        releaseSlot();
      }
    })();
    jobs.set(key, job);
  }
  return job;
};

// The page image if it has already been drawn.
export const renderedPdfFirstPage = (key: string) => rendered.get(key) ?? null;
